<template>
  <div class="electrostatic-potential-viz">
    
    <div class="viz-header" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula" v-latex>$<strong>公式：</strong>φ = kQ/r | Ep = qφ$</div>
    </div>

    <div class="main-layout vertical">
      <!-- 2D 场图模拟 -->
      <div class="canvas-mixed interactive" @mousedown="startDrag" @mousemove="onDrag" @mouseup="stopDrag">
        <svg width="100%" height="280" viewBox="0 0 400 280">
           <!-- 电场线 (示意) -->
           <line v-for="i in 12" :key="'f'+i" x1="200" y1="140" 
             :x2="200 + 300 * Math.cos(i*Math.PI/6)" 
             :y2="140 + 300 * Math.sin(i*Math.PI/6)" 
             stroke="rgba(0, 210, 255, 0.1)" stroke-dasharray="2,2" />

           <!-- 等势面 (环形) -->
           <circle v-for="r in [40, 70, 100, 130]" :key="'e'+r" cx="200" cy="140" :r="r" fill="none" stroke="rgba(255, 215, 0, 0.2)" stroke-width="1" />

           <!-- 中心电荷 Q -->
           <circle cx="200" cy="140" r="10" :fill="sourceQ > 0 ? '#ff4757' : '#00d2ff'" />
           <text x="195" y="145" fill="#fff" font-size="12" font-weight="bold">{{ sourceQ > 0 ? '+' : '-' }}</text>

           <!-- 试探电荷 q (可拖动) -->
           <g :transform="`translate(${qPos.x}, ${qPos.y})`" style="cursor: move">
              <circle r="6" :fill="testQ > 0 ? '#fa8231' : '#a55eea'" />
              <text y="-10" text-anchor="middle" fill="#fff" font-size="10">q</text>
           </g>

           <!-- 势能指示线 -->
           <line :x1="200" :y1="140" :x2="qPos.x" :y2="qPos.y" stroke="rgba(255,255,255,0.1)" stroke-dasharray="5,5" />
        </svg>

        <div class="hud-mini">
           <div class="item"><span>电势 φ:</span> <span class="val">{{ potential.toFixed(1) }} V</span></div>
           <div class="item"><span>电势能 Ep:</span> <span class="val">{{ energy.toFixed(1) }} J</span></div>
        </div>
      </div>

      <!-- 参数控制 -->
      <div class="bottom-controls">
         <div class="control-card glass">
            <div class="row">
               <div class="col">
                  <label>中心电荷 Q (μC)</label>
                  <el-slider v-model="sourceQ" :min="-10" :max="10" size="mini"></el-slider>
               </div>
               <div class="col">
                  <label>试探电荷 q (μC)</label>
                  <el-slider v-model="testQ" :min="-5" :max="5" size="mini"></el-slider>
               </div>
            </div>
         </div>

         <div class="theory-note">
            <p><strong>物理演示：</strong>拖动小电荷 <strong>q</strong>。越靠近同性中心电荷，电场力做负功，<strong>电势能</strong>增加；越靠近异性中心电荷，电场力做正功，电势能降低。</p>
         </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ElectricPotentialViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      sourceQ: 5,
      testQ: 2,
      qPos: { x: 300, y: 100 },
      isDragging: false
    }
  },
  computed: {
    distanceSq() {
       const dx = this.qPos.x - 200;
       const dy = this.qPos.y - 140;
       return Math.max(100, dx*dx + dy*dy);
    },
    distance() { return Math.sqrt(this.distanceSq); },
    potential() {
       // phi = kQ / r
       return (900 * this.sourceQ) / (this.distance / 10);
    },
    energy() {
       // Ep = q * phi
       return (this.testQ * this.potential) / 10;
    }
  },
  methods: {
    startDrag() { this.isDragging = true; },
    stopDrag() { this.isDragging = false; },
    onDrag(e) {
       if (!this.isDragging) return;
       const rect = e.currentTarget.getBoundingClientRect();
       this.qPos.x = e.clientX - rect.left;
       this.qPos.y = e.clientY - rect.top;
    }
  }
}
</script>

<style scoped lang="scss">
.electrostatic-potential-viz { flex: 1; display: flex; flex-direction: column; gap: 12px; height: 100%; overflow: hidden; }
.viz-header { display: flex; justify-content: space-between; align-items: center; .title { font-size: 1rem; color: #fff; } .formula-chip { color: #ffd700; background: rgba(255,215,0,0.1); padding: 4px 12px; border-radius: 8px; font-weight: bold; } }
.canvas-mixed { 
   flex: 1.3; background: #050510; border-radius: 12px; position: relative; border: 1px solid rgba(255,255,255,0.05);
   .hud-mini { position: absolute; top: 15px; left: 15px; .val { color: #00d2ff; font-weight: bold; font-family: monospace; } }
}
.bottom-controls { flex: 0.7; display: flex; flex-direction: column; gap: 10px; }
.control-card { background: rgba(255,255,255,0.03); border-radius: 12px; padding: 15px; .row { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; label { font-size: 0.7rem; color: #888; display: block; margin-bottom: 5px; } } }
.theory-note { background: rgba(0, 210, 255, 0.05); padding: 12px 18px; border-radius: 12px; p { margin: 0; font-size: 0.75rem; color: #aaa; line-height: 1.5; strong { color: #fff; } } }
</style>
